import React, { useEffect, useRef, useCallback, useMemo, useState } from 'react';
import { Card, CardContent, CardHeader } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '../../components/ui/select';
import { Switch } from '../../components/ui/switch';
import { Label } from '../../components/ui/label';
import { cn } from "@/lib/utils";
import AfterTaxIncomeChart from '../../charts/AfterTaxIncomeChart';
import { RetirementFinancialInputs } from '../../Types/Autogenerated/AfterTaxRetirementIncomeOptimizerRequestPayload';
import { AfterTaxRetirementIncomeOptimizerAPIResponse } from '../../Types/Autogenerated/AfterTaxRetirementIncomeOptimizerApiResponse';
import { Convert as FilingStatusConvert } from '../../Types/Autogenerated/FilingStatus';
import { Convert as StateConvert } from '../../Types/Autogenerated/State';
import { FormData } from '../../App';
import { CurrencyInputOnChangeValues } from 'react-currency-input-field';
import { taxYearItems, stateItems, filingStatuses } from '../../Utils/Constants';
import TermsDescriptions from '../../components/TermsDescriptions';
import { afterTaxRetirementTerms } from '../../Utils/TermDefinitions';
import FloatingChartButton from '../../components/FloatingChartButton';
import ChartSummary from '../../components/ChartSummary';
import { generateAfterTaxRetirementSummary } from '../../Utils/ChartSummaryGenerators';
import { formatCurrency } from '../../Utils/chartUtils';


interface AfterTaxRetirementIncomeOptimizerPageProps {
    formData: FormData;
    selectedPage: string; // Should always be 'After-Tax Retirement Income' when this component is displayed
    personsToggle: boolean;
    adjustYearlyForInflationToggle: boolean;
    showSnackbar: boolean;
    snackbarMessage: string;
    chartData: number[][] | null;
    buttonStyle: { margin: number; };
    handleClose: (event: React.SyntheticEvent | Event, reason?: string) => void;
    setChartData: (value: React.SetStateAction<number[][] | null>) => void;
    handleClick: () => void;
    // Set error messges in the snackbar
    setSnackbarMessage: (value: React.SetStateAction<string>) => void;
    // Primary handler for handling the change in an input value
    handleInputChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
    // onValueChange is used instead of onChange because of how the user-input is modified by CurrencyInput,
    // which seems to cause onChange to not always be triggered appriopriately as commas are shifted around
    onValueChange: (value: string | undefined, name?: string, values?: CurrencyInputOnChangeValues) => void;
    // Hanlder for when a toggle is changed
    handleToggleChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
    // Hanlder for when a select is changed
    handleSelectChange: (event: React.ChangeEvent<HTMLSelectElement>) => void;
  }
    

const AfterTaxRetirementIncomeOptimizerPage: React.FC<AfterTaxRetirementIncomeOptimizerPageProps> = ({ 
    formData, 
    selectedPage,
    personsToggle, 
    adjustYearlyForInflationToggle, 
    showSnackbar,
    snackbarMessage,
    chartData,
    buttonStyle,
    handleClose,
    setChartData,
    handleClick,
    setSnackbarMessage, 
    handleInputChange, 
    onValueChange, 
    handleToggleChange,
    handleSelectChange,
}) => {
  const chartRef = useRef<HTMLDivElement>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [apiResponseData, setApiResponseData] = useState<AfterTaxRetirementIncomeOptimizerAPIResponse | null>(null);

  const formatNumber = (num: number | string) => {
    if (typeof num === 'string') {
      num = parseFloat(num.replace(/,/g, ''));
    }
    return isNaN(num) ? '' : num.toLocaleString('en-US');
  };

  const handleFinancialInputChange = (value: string, name: string) => {
    const numericValue = value ? parseFloat(value.replace(/,/g, '')) : 0;
    onValueChange(numericValue.toString(), name);
  };

  // Function to render inputs based on the number of persons
  const renderInputs = () => {
    return (
      <div className="space-y-6">
        {/* Account Configuration */}
        <div className="p-4 rounded-lg bg-muted/30 border border-border">
          <div className={cn("flex items-center justify-between")}>
            <div>
              <Label htmlFor="personsToggle" className="text-sm font-medium text-foreground">
                Multiple Retirement Portfolios
              </Label>
              <p className="text-xs text-muted-foreground mt-1">
                Optimize for dual retirement portfolios with different accounts
              </p>
            </div>
            <Switch
              id="personsToggle"
              checked={personsToggle}
              onCheckedChange={(checked) => handleToggleChange({
                target: { name: 'personsToggle', checked, id: 'persons', type: 'checkbox' }
              } as React.ChangeEvent<HTMLInputElement>)}
            />
          </div>
        </div>

        {/* Current Account Balances Section */}
        <div className="space-y-4">
          <div className="flex items-center gap-2 mb-3">
            <h3 className="text-sm font-semibold text-foreground">Current Account Balances</h3>
            <div className="h-px bg-border flex-1"></div>
          </div>
          
          <div className={cn("grid gap-6", personsToggle ? "grid-cols-1 sm:grid-cols-2" : "grid-cols-1")}>
            <div className={cn("space-y-4")}>
              <div className="grid grid-cols-1 gap-3">
                {renderProfessionalFinancialInput(`${personsToggle ? 'Primary ' : ''}Traditional Balance`, "initialTradBalance", personsToggle ? "initialTradBalancePerson1" : "initialTradBalance", "Current balance in traditional tax-deferred accounts, including 401k, IRA, etc.", "50,000")}
                {renderProfessionalFinancialInput(`${personsToggle ? 'Primary ' : ''}Roth Balance`, "initialRothBalance", personsToggle ? "initialRothBalancePerson1" : "initialRothBalance", "Current balance in Roth tax-free accounts, including Roth 401k, Roth IRA, etc.", "25,000")}
                {renderProfessionalFinancialInput(`${personsToggle ? 'Primary ' : ''}Brokerage Balance`, "initialBrokerageBalance", personsToggle ? "initialBrokerageBalancePerson1" : "initialBrokerageBalance", "Current balance in taxable investment accounts", "10,000")}
              </div>
            </div>
            
            {personsToggle && (
              <div className={cn("space-y-4 border-l-2 border-border pl-4")}>
                <div className="grid grid-cols-1 gap-3">
                  {renderProfessionalFinancialInput("Secondary Traditional Balance", "initialTradBalancePerson2", "initialTradBalancePerson2", "Current balance in traditional tax-deferred accounts, including 401k, IRA, etc.", "40,000")}
                  {renderProfessionalFinancialInput("Secondary Roth Balance", "initialRothBalancePerson2", "initialRothBalancePerson2", "Current balance in Roth tax-free accounts, including Roth 401k, Roth IRA, etc.", "20,000")}
                  {renderProfessionalFinancialInput("Secondary Brokerage Balance", "initialBrokerageBalancePerson2", "initialBrokerageBalancePerson2", "Current balance in taxable investment accounts", "8,000")}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Annual Contributions Section */}
        <div className="space-y-4">
          <div className="flex items-center gap-2 mb-3">
            <h3 className="text-sm font-semibold text-foreground">Annual Contributions</h3>
            <div className="h-px bg-border flex-1"></div>
          </div>
          
          <div className={cn("grid gap-6", personsToggle ? "grid-cols-1 sm:grid-cols-2" : "grid-cols-1")}>
            <div className={cn("space-y-4")}>
              <div className="grid grid-cols-1 gap-3">
                {renderProfessionalFinancialInput(`${personsToggle ? 'Primary ' : ''}Traditional 401k Contribution`, "yearlyTrad401k", personsToggle ? "yearlyTrad401kPerson1" : "yearlyTrad401k", "Annual pre-tax 401k contributions", "15,000")}
                {renderProfessionalFinancialInput(`${personsToggle ? 'Primary ' : ''}Roth 401k Contribution`, "yearlyRoth401k", personsToggle ? "yearlyRoth401kPerson1" : "yearlyRoth401k", "Annual Roth 401k contributions", "5,000")}
                {renderProfessionalFinancialInput(`${personsToggle ? 'Primary ' : ''}401k Company Match`, "yearlyTrad401kCompanyMatch", personsToggle ? "yearlyTrad401kCompanyMatchPerson1" : "yearlyTrad401kCompanyMatch", "Annual employer 401k matching", "3,000")}
                {renderProfessionalFinancialInput(`${personsToggle ? 'Primary ' : ''}After-Tax 401k Contribution`, "yearlyAfterTax401k", personsToggle ? "yearlyAfterTax401kPerson1" : "yearlyAfterTax401k", "Annual after-tax 401k contributions (mega backdoor)", "10,000")}
                {renderProfessionalFinancialInput(`${personsToggle ? 'Primary ' : ''}Traditional IRA Contribution`, "yearlyTradIRA", personsToggle ? "yearlyTradIRAPerson1" : "yearlyTradIRA", "Annual traditional IRA contributions", "6,000")}
                {renderProfessionalFinancialInput(`${personsToggle ? 'Primary ' : ''}Roth IRA Contribution`, "yearlyRothIRA", personsToggle ? "yearlyRothIRAPerson1" : "yearlyRothIRA", "Annual Roth IRA contributions", "6,000")}
                {renderProfessionalFinancialInput(`${personsToggle ? 'Primary ' : ''}Brokerage Investment`, "yearlyBrokerage", personsToggle ? "yearlyBrokeragePerson1" : "yearlyBrokerage", "Annual taxable account investments", "5,000")}
              </div>
            </div>
            
            {personsToggle && (
              <div className={cn("space-y-4 border-l-2 border-border pl-4")}>
                <div className="grid grid-cols-1 gap-3">
                  {renderProfessionalFinancialInput("Secondary Traditional 401k Contribution", "yearlyTrad401kPerson2", "yearlyTrad401kPerson2", "Annual pre-tax 401k contributions", "12,000")}
                  {renderProfessionalFinancialInput("Secondary Roth 401k Contribution", "yearlyRoth401kPerson2", "yearlyRoth401kPerson2", "Annual Roth 401k contributions", "3,000")}
                  {renderProfessionalFinancialInput("Secondary 401k Company Match", "yearlyTrad401kCompanyMatchPerson2", "yearlyTrad401kCompanyMatchPerson2", "Annual employer 401k matching", "2,500")}
                  {renderProfessionalFinancialInput("Secondary After-Tax 401k Contribution", "yearlyAfterTax401kPerson2", "yearlyAfterTax401kPerson2", "Annual after-tax 401k contributions (mega backdoor)", "8,000")}
                  {renderProfessionalFinancialInput("Secondary Traditional IRA Contribution", "yearlyTradIRAPerson2", "yearlyTradIRAPerson2", "Annual traditional IRA contributions", "6,000")}
                  {renderProfessionalFinancialInput("Secondary Roth IRA Contribution", "yearlyRothIRAPerson2", "yearlyRothIRAPerson2", "Annual Roth IRA contributions", "6,000")}
                  {renderProfessionalFinancialInput("Secondary Brokerage Investment", "yearlyBrokeragePerson2", "yearlyBrokeragePerson2", "Annual taxable account investments", "3,000")}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Personal Information Section */}
        <div className="space-y-4">
          <div className="flex items-center gap-2 mb-3">
            <h3 className="text-sm font-semibold text-foreground">Personal Information</h3>
            <div className="h-px bg-border flex-1"></div>
          </div>
          
          <div className={cn("grid gap-6", personsToggle ? "grid-cols-1 sm:grid-cols-2" : "grid-cols-1")}>
            <div className={cn("space-y-4")}>
              <div className="grid grid-cols-1 gap-3">
                {renderProfessionalFinancialInput(`${personsToggle ? 'Primary ' : ''}Annual Income`, "income", personsToggle ? "incomePerson1" : "income", "Annual gross income before taxes", "100,000")}
                {renderProfessionalNumberInput(`${personsToggle ? 'Primary ' : ''}Current Age`, "currentAge", personsToggle ? "currentAgePerson1" : "currentAge", "Your current age", "35")}
                {renderProfessionalNumberInput(`${personsToggle ? 'Primary ' : ''}Stop Saving Age`, "contributionsEndAge", personsToggle ? "contributionsEndAgePerson1" : "contributionsEndAge", "Age when you stop contributing to retirement accounts", "65")}
                {renderProfessionalNumberInput(`${personsToggle ? 'Primary ' : ''}Retirement Age`, "withdrawalStartAge", personsToggle ? "withdrawalStartAgePerson1" : "withdrawalStartAge", "Age when you start withdrawing for retirement", "67")}
              </div>
            </div>
            
            {personsToggle && (
              <div className={cn("space-y-4 border-l-2 border-border pl-4")}>
                <div className="grid grid-cols-1 gap-3">
                  {renderProfessionalFinancialInput("Secondary Annual Income", "incomePerson2", "incomePerson2", "Annual gross income before taxes", "80,000")}
                  {renderProfessionalNumberInput("Secondary Current Age", "currentAgePerson2", "currentAgePerson2", "Current age", "32")}
                  {renderProfessionalNumberInput("Secondary Stop Saving Age", "contributionsEndAgePerson2", "contributionsEndAgePerson2", "Age when stopping retirement contributions", "62")}
                  {renderProfessionalNumberInput("Secondary Retirement Age", "withdrawalStartAgePerson2", "withdrawalStartAgePerson2", "Age when starting retirement withdrawals", "65")}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Financial Parameters Section */}
        <div className="space-y-4">
          <div className="flex items-center gap-2 mb-3">
            <h3 className="text-sm font-semibold text-foreground">Financial Parameters</h3>
            <div className="h-px bg-border flex-1"></div>
          </div>
          
          <div className="p-4 rounded-lg bg-muted/30 border border-border">
            <div className={cn("flex items-center justify-between")}>
              <div>
                <Label htmlFor="adjustYearlyForInflationToggle" className="text-sm font-medium text-foreground">
                  Inflation-Adjusted Contributions
                </Label>
                <p className="text-xs text-muted-foreground mt-1">
                  Increase annual contributions to keep pace with inflation
                </p>
              </div>
              <Switch
                id="adjustYearlyForInflationToggle"
                checked={adjustYearlyForInflationToggle}
                onCheckedChange={(checked) => handleToggleChange({
                  target: { name: 'adjustYearlyForInflationToggle', checked, id: 'adjustYearlyForInflationToggle', type: 'checkbox' }
                } as React.ChangeEvent<HTMLInputElement>)}
              />
            </div>
          </div>
          
          <div className={cn("grid grid-cols-1 sm:grid-cols-3 gap-4")}>
            {renderProfessionalPercentageInput("Withdrawal Rate", "withdrawalRate", "Percentage of portfolio withdrawn annually in retirement", "4.0")}
            {renderProfessionalPercentageInput("Rate of Return", "averageAnnualReturn", "Expected average annual investment return", "7.0")}
            {renderProfessionalPercentageInput("Inflation Rate", "inflationRate", "Expected average annual inflation rate", "3.0")}
          </div>
        </div>

        {/* Tax Configuration Section */}
        <div className="space-y-4">
          <div className="flex items-center gap-2 mb-3">
            <h3 className="text-sm font-semibold text-foreground">Tax Configuration</h3>
            <div className="h-px bg-border flex-1"></div>
          </div>
          
          <div className={cn("grid grid-cols-1 sm:grid-cols-3 gap-4")}>
            {renderProfessionalDropdown("Tax Year", "taxYear", taxYearItems)}
            {renderProfessionalDropdown("State of Residence", "state", stateItems)}
            {renderProfessionalDropdown("Filing Status", "filingStatus", filingStatuses)}
          </div>
        </div>

      </div>
    );
  };

  const renderProfessionalFinancialInput = (label: string, name: string, formDataKey: string, description: string, placeholder: string) => (
    <div className="space-y-2">
      <Label htmlFor={name} className="text-sm font-medium text-foreground flex items-center gap-2">
        {label}
        <span className="text-xs text-muted-foreground">({description})</span>
      </Label>
      <div className={cn("relative group")}>
        <span className={cn("absolute inset-y-0 left-0 flex items-center pl-3 text-muted-foreground text-sm group-focus-within:text-primary transition-colors")}>$</span>
        <Input
          id={name}
          name={formDataKey}
          type="text"
          placeholder={placeholder}
          value={formatNumber(String(formData[formDataKey as keyof FormData]))}
          onChange={(e) => handleFinancialInputChange(e.target.value, formDataKey)}
          className={cn("pl-8 text-lg font-medium")}
        />
      </div>
    </div>
  );

  const renderProfessionalNumberInput = (label: string, name: string, formDataKey: string, description: string, placeholder: string) => (
    <div className="space-y-2">
      <Label htmlFor={name} className="text-sm font-medium text-foreground flex items-center gap-2">
        {label}
        <span className="text-xs text-muted-foreground">({description})</span>
      </Label>
      <Input
        id={name}
        name={formDataKey}
        type="number"
        placeholder={placeholder}
        value={String(formData[formDataKey as keyof FormData])}
        onChange={handleInputChange}
        className={cn("text-lg font-medium")}
      />
    </div>
  );

  const renderProfessionalPercentageInput = (label: string, name: string, description: string, placeholder: string) => (
    <div className="space-y-2">
      <Label htmlFor={name} className="text-sm font-medium text-foreground flex items-center gap-2">
        {label}
        <span className="text-xs text-muted-foreground">({description})</span>
      </Label>
      <div className={cn("relative group")}>
        <Input
          id={name}
          name={name}
          type="number"
          placeholder={placeholder}
          value={String(formData[name as keyof FormData])}
          onChange={handleInputChange}
          className={cn("pr-8")}
        />
        <span className={cn("absolute inset-y-0 right-0 flex items-center pr-3 text-muted-foreground text-sm group-focus-within:text-primary transition-colors")}>%</span>
      </div>
    </div>
  );

  const sortedTaxYears = Object.entries(taxYearItems)
    .sort((a, b) => parseInt(b[1]) - parseInt(a[1]));

  const renderProfessionalDropdown = (label: string, name: string, options: Record<string, string>) => (
    <div className="space-y-2">
      <Label htmlFor={name} className="text-sm font-medium text-foreground">{label}</Label>
      <Select 
        value={formData[name as keyof FormData] as string}
        onValueChange={(value) => handleSelectChange({ target: { name, value } } as React.ChangeEvent<HTMLSelectElement>)}
      >
        <SelectTrigger id={name} name={name} className="h-11">
          <SelectValue placeholder={`Select ${label}`} />
        </SelectTrigger>
        <SelectContent>
          {name === 'taxYear'
            ? sortedTaxYears.map(([key, value]) => (
                <SelectItem key={value} value={value}>{key}</SelectItem>
              ))
            : Object.entries(options).map(([value, label]) => (
                <SelectItem key={value} value={value}>{label}</SelectItem>
              ))
            }
        </SelectContent>
      </Select>
    </div>
  );

  const labels: string[] = [
    "Income Roth",
    "Income Trad",
    "Income Brokerage",
    "Income Post Tax"
  ];  

  // Function to fetch and parse the data
  const fetchData = useCallback(async (url: string, fetchOptions: any): Promise<AfterTaxRetirementIncomeOptimizerAPIResponse> => {
    const response = await fetch(url, fetchOptions);
    if (!response.ok) {
      const errorData = await response.json();
      const errorMessage: string = errorData.error || 'Unknown error occurred'
      setSnackbarMessage(errorMessage)
      throw new Error(errorMessage);
    }

    const data = await response.json();
    return data as AfterTaxRetirementIncomeOptimizerAPIResponse;
  }, [setSnackbarMessage]);
  const makeAfterTaxIncomeOptimizerUrl = () => {
    // For local development, use localhost
    if (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') {
      return 'http://127.0.0.1:5000/RetirementOptimizer';
    }
    
    // For deployed environments, use the CloudFront API path
    return `https://${window.location.hostname}/api/retirement-optimizer`;
  }


  // Memoize input data to prevent unnecessary recalculations
  const inputData = useMemo(() => {
    const singlePersonData = {
      initialRothBalance: formData.initialRothBalance,
      initialTradBalance: formData.initialTradBalance,
      initialBrokerageBalance: formData.initialBrokerageBalance,
      yearlyRoth401k: formData.yearlyRoth401k,
      yearlyTrad401k: formData.yearlyTrad401k,
      yearlyTrad401kCompanyMatch: formData.yearlyTrad401kCompanyMatch,
      yearlyAfterTax401k: formData.yearlyAfterTax401k,
      yearlyRothIRA: formData.yearlyRothIRA,
      yearlyTradIRA: formData.yearlyTradIRA,
      yearlyBrokerage: formData.yearlyBrokerage,
      currentAge: formData.currentAge,
      contributionsEndAge: formData.contributionsEndAge,
      withdrawalStartAge: formData.withdrawalStartAge,
      income: formData.income,
    };

    const twoPersonData = {
      person1: {
        initialRothBalance: formData.initialRothBalancePerson1,
        initialTradBalance: formData.initialTradBalancePerson1,
        initialBrokerageBalance: formData.initialBrokerageBalancePerson1,
        yearlyRoth401k: formData.yearlyRoth401kPerson1,
        yearlyTrad401k: formData.yearlyTrad401kPerson1,
        yearlyTrad401kCompanyMatch: formData.yearlyTrad401kCompanyMatchPerson1,
        yearlyAfterTax401k: formData.yearlyAfterTax401kPerson1,
        yearlyRothIRA: formData.yearlyRothIRAPerson1,
        yearlyTradIRA: formData.yearlyTradIRAPerson1,
        yearlyBrokerage: formData.yearlyBrokeragePerson1,
        currentAge: formData.currentAgePerson1,
        contributionsEndAge: formData.contributionsEndAgePerson1,
        withdrawalStartAge: formData.withdrawalStartAgePerson1,
        income: formData.incomePerson1,
      },
      person2: {
        initialRothBalance: formData.initialRothBalancePerson2,
        initialTradBalance: formData.initialTradBalancePerson2,
        initialBrokerageBalance: formData.initialBrokerageBalancePerson2,
        yearlyRoth401k: formData.yearlyRoth401kPerson2,
        yearlyTrad401k: formData.yearlyTrad401kPerson2,
        yearlyTrad401kCompanyMatch: formData.yearlyTrad401kCompanyMatchPerson2,
        yearlyAfterTax401k: formData.yearlyAfterTax401kPerson2,
        yearlyRothIRA: formData.yearlyRothIRAPerson2,
        yearlyTradIRA: formData.yearlyTradIRAPerson2,
        yearlyBrokerage: formData.yearlyBrokeragePerson2,
        currentAge: formData.currentAgePerson2,
        contributionsEndAge: formData.contributionsEndAgePerson2,
        withdrawalStartAge: formData.withdrawalStartAgePerson2,
        income: formData.incomePerson2,
      }
    };

    return {
      personsToggle,
      adjustYearlyForInflationToggle,
      singlePersonData,
      twoPersonData,
      filingStatus: formData.filingStatus,
      inflationRate: formData.inflationRate,
      averageAnnualReturn: formData.averageAnnualReturn,
      state: formData.state,
      taxYear: formData.taxYear,
      withdrawalRate: formData.withdrawalRate,
    };
  }, [
    personsToggle,
    adjustYearlyForInflationToggle,
    // Single person data
    formData.initialRothBalance,
    formData.initialTradBalance,
    formData.initialBrokerageBalance,
    formData.yearlyRoth401k,
    formData.yearlyTrad401k,
    formData.yearlyTrad401kCompanyMatch,
    formData.yearlyAfterTax401k,
    formData.yearlyRothIRA,
    formData.yearlyTradIRA,
    formData.yearlyBrokerage,
    formData.currentAge,
    formData.contributionsEndAge,
    formData.withdrawalStartAge,
    formData.income,
    // Two person data
    formData.initialRothBalancePerson1,
    formData.initialTradBalancePerson1,
    formData.initialBrokerageBalancePerson1,
    formData.yearlyRoth401kPerson1,
    formData.yearlyTrad401kPerson1,
    formData.yearlyTrad401kCompanyMatchPerson1,
    formData.yearlyAfterTax401kPerson1,
    formData.yearlyRothIRAPerson1,
    formData.yearlyTradIRAPerson1,
    formData.yearlyBrokeragePerson1,
    formData.currentAgePerson1,
    formData.contributionsEndAgePerson1,
    formData.withdrawalStartAgePerson1,
    formData.incomePerson1,
    formData.initialRothBalancePerson2,
    formData.initialTradBalancePerson2,
    formData.initialBrokerageBalancePerson2,
    formData.yearlyRoth401kPerson2,
    formData.yearlyTrad401kPerson2,
    formData.yearlyTrad401kCompanyMatchPerson2,
    formData.yearlyAfterTax401kPerson2,
    formData.yearlyRothIRAPerson2,
    formData.yearlyTradIRAPerson2,
    formData.yearlyBrokeragePerson2,
    formData.currentAgePerson2,
    formData.contributionsEndAgePerson2,
    formData.withdrawalStartAgePerson2,
    formData.incomePerson2,
    // Common data
    formData.filingStatus,
    formData.inflationRate,
    formData.averageAnnualReturn,
    formData.state,
    formData.taxYear,
    formData.withdrawalRate,
  ]);

  // Create the payload from memoized input data
  const payload = useMemo(() => {
    const retirementFinancialInputs: RetirementFinancialInputs[] = !inputData.personsToggle
      ? [{
          initial_roth_balance: parseFloat(inputData.singlePersonData.initialRothBalance),
          initial_trad_balance: parseFloat(inputData.singlePersonData.initialTradBalance),
          initial_brokerage_balance: parseFloat(inputData.singlePersonData.initialBrokerageBalance),
          yearly_roth_401k: parseFloat(inputData.singlePersonData.yearlyRoth401k),
          yearly_trad_401k: parseFloat(inputData.singlePersonData.yearlyTrad401k),
          yearly_trad_401k_company_match: parseFloat(inputData.singlePersonData.yearlyTrad401kCompanyMatch),
          yearly_after_tax_401k: parseFloat(inputData.singlePersonData.yearlyAfterTax401k),
          yearly_roth_ira: parseFloat(inputData.singlePersonData.yearlyRothIRA),
          yearly_trad_ira: parseFloat(inputData.singlePersonData.yearlyTradIRA),
          yearly_brokerage: parseFloat(inputData.singlePersonData.yearlyBrokerage),
          current_age: parseInt(inputData.singlePersonData.currentAge),
          contributions_end_age: parseInt(inputData.singlePersonData.contributionsEndAge),
          withdrawal_start_age: parseInt(inputData.singlePersonData.withdrawalStartAge),
          income: parseFloat(inputData.singlePersonData.income),
        }]
      : [{
          initial_roth_balance: parseFloat(inputData.twoPersonData.person1.initialRothBalance),
          initial_trad_balance: parseFloat(inputData.twoPersonData.person1.initialTradBalance),
          initial_brokerage_balance: parseFloat(inputData.twoPersonData.person1.initialBrokerageBalance),
          yearly_roth_401k: parseFloat(inputData.twoPersonData.person1.yearlyRoth401k),
          yearly_trad_401k: parseFloat(inputData.twoPersonData.person1.yearlyTrad401k),
          yearly_trad_401k_company_match: parseFloat(inputData.twoPersonData.person1.yearlyTrad401kCompanyMatch),
          yearly_after_tax_401k: parseFloat(inputData.twoPersonData.person1.yearlyAfterTax401k),
          yearly_roth_ira: parseFloat(inputData.twoPersonData.person1.yearlyRothIRA),
          yearly_trad_ira: parseFloat(inputData.twoPersonData.person1.yearlyTradIRA),
          yearly_brokerage: parseFloat(inputData.twoPersonData.person1.yearlyBrokerage),
          current_age: parseInt(inputData.twoPersonData.person1.currentAge),
          contributions_end_age: parseInt(inputData.twoPersonData.person1.contributionsEndAge),
          withdrawal_start_age: parseInt(inputData.twoPersonData.person1.withdrawalStartAge),
          income: parseFloat(inputData.twoPersonData.person1.income),
        }, {
          initial_roth_balance: parseFloat(inputData.twoPersonData.person2.initialRothBalance),
          initial_trad_balance: parseFloat(inputData.twoPersonData.person2.initialTradBalance),
          initial_brokerage_balance: parseFloat(inputData.twoPersonData.person2.initialBrokerageBalance),
          yearly_roth_401k: parseFloat(inputData.twoPersonData.person2.yearlyRoth401k),
          yearly_trad_401k: parseFloat(inputData.twoPersonData.person2.yearlyTrad401k),
          yearly_trad_401k_company_match: parseFloat(inputData.twoPersonData.person2.yearlyTrad401kCompanyMatch),
          yearly_after_tax_401k: parseFloat(inputData.twoPersonData.person2.yearlyAfterTax401k),
          yearly_roth_ira: parseFloat(inputData.twoPersonData.person2.yearlyRothIRA),
          yearly_trad_ira: parseFloat(inputData.twoPersonData.person2.yearlyTradIRA),
          yearly_brokerage: parseFloat(inputData.twoPersonData.person2.yearlyBrokerage),
          current_age: parseInt(inputData.twoPersonData.person2.currentAge),
          contributions_end_age: parseInt(inputData.twoPersonData.person2.contributionsEndAge),
          withdrawal_start_age: parseInt(inputData.twoPersonData.person2.withdrawalStartAge),
          income: parseFloat(inputData.twoPersonData.person2.income),
        }];

    return {
      adjust_yearly_for_inflation: inputData.adjustYearlyForInflationToggle,
      filing_status: FilingStatusConvert.toFilingStatus(JSON.stringify(inputData.filingStatus)),
      inflation: parseFloat(inputData.inflationRate) / 100,
      interest: parseFloat(inputData.averageAnnualReturn) / 100,
      retirement_financial_inputs: retirementFinancialInputs,
      state: StateConvert.toState(JSON.stringify(inputData.state)),
      tax_year: parseInt(inputData.taxYear),
      withdrawal_rate: parseFloat(inputData.withdrawalRate) / 100,
    };
  }, [inputData]);

  const processChartData = useCallback((data: AfterTaxRetirementIncomeOptimizerAPIResponse): number[][] => {
    // Process the data to a format suitable for your chart
    
    // Extract data for the graph
    // Extract the relevant labels from the API response
    const chartData = [
      [
        data.current.income_roth,
        data.current.income_trad,
        data.current.income_brokerage,
        data.current.income_post_tax,
      ],
      [
        data.optimized.income_roth,
        data.optimized.income_trad,
        data.optimized.income_brokerage,
        data.optimized.income_post_tax,
      ]
    ];

  return chartData
  }, []);

  const isLoadingRef = useRef(false);

  const makeAPIRequest = useCallback(async () => {
    if (isLoadingRef.current) return; // Prevent multiple simultaneous requests
    
    isLoadingRef.current = true;
    setIsLoading(true);
    try {
      const url = makeAfterTaxIncomeOptimizerUrl();
      const fetchOptions = {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload)
      };

      const data = await fetchData(url, fetchOptions);
      setApiResponseData(data);
      setChartData(processChartData(data));
    } catch (error) {
      console.error('Error:', error);
      handleClick();
    } finally {
      isLoadingRef.current = false;
      setIsLoading(false);
    }
  }, [payload, fetchData, processChartData, setChartData, handleClick]);

  // Track previous input data for change detection
  const previousInputDataRef = useRef<string>('');

  // Auto-calculate with debouncing for larger API calls - only when inputs actually change
  useEffect(() => {
    const currentInputDataString = JSON.stringify(inputData);
    
    // Only make API request if input data has actually changed
    if (currentInputDataString !== previousInputDataRef.current) {
      const timeoutId = setTimeout(() => {
        makeAPIRequest();
        previousInputDataRef.current = currentInputDataString;
      }, 1000); // 1000ms debounce for larger API calls

      return () => clearTimeout(timeoutId);
    }
  }, [inputData, makeAPIRequest]);

  return (
    <div className="after-tax-retirement-income-optimizer w-full px-2 sm:px-4 lg:px-6 financial-app">
      {/* Professional Header */}
      <div className="hero-section mb-6 -mx-2 sm:-mx-4 lg:-mx-6 px-2 sm:px-4 lg:px-6">
        <div className="professional-layout">
          <h1 className="text-3xl sm:text-4xl font-bold text-foreground mb-2 tracking-tight">
            {selectedPage}
          </h1>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto leading-relaxed">
            Maxmimize your after-tax retirement income by changing how you place your retirement savings
          </p>
        </div>
      </div>

      <div className="flex flex-col lg:flex-row gap-6 mb-6">
        {/* Form Section */}
        <div className="lg:w-2/5 min-w-0">
          <Card className="w-full shadow-lg hover:shadow-xl transition-all duration-300">
            <CardHeader className="pb-4">
              <div className="flex items-center gap-3">
                <div className="w-2 h-8 bg-gradient-to-b from-primary to-accent rounded-full"></div>
                <h2 className={cn("text-xl font-semibold text-card-foreground")}>Retirement Optimizer</h2>
              </div>
              <p className="text-sm text-muted-foreground mt-2">
                Configure all your retirement accounts and preferences for optimization analysis
              </p>
            </CardHeader>
            <CardContent className="flex-grow overflow-y-auto">
              {renderInputs()}
            </CardContent>
          </Card>
        </div>

        {/* Enhanced Chart Section */}
        <div className="lg:w-3/5 min-w-0" ref={chartRef}>
          <Card className="w-full h-full shadow-lg hover:shadow-xl transition-all duration-300">
            <CardHeader className="pb-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-2 h-8 bg-gradient-to-b from-accent to-primary rounded-full"></div>
                  <div>
                    <h2 className="text-lg font-semibold text-card-foreground">Income Forecasts</h2>
                    <p className="text-sm text-muted-foreground">Current vs optimized</p>
                  </div>
                </div>
                {chartData && !isLoading && apiResponseData && (
                  <div className="text-right space-y-3">
                    <div>
                      <div className="text-sm text-muted-foreground">Current</div>
                      <div className="text-xl font-bold" style={{ color: 'rgba(54, 162, 235, 1)' }}>
                        {formatCurrency(apiResponseData.current?.income_post_tax || 0)}
                      </div>
                    </div>
                    <div>
                      <div className="text-sm text-muted-foreground">Tax Optimized</div>
                      <div className="text-xl font-bold" style={{ color: 'rgba(75, 192, 192, 1)' }}>
                        {formatCurrency(apiResponseData.optimized?.income_post_tax || 0)}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </CardHeader>
            <CardContent className="h-full">
              {chartData && !isLoading ? (
                <div className="h-full flex flex-col">
                  <div className="flex-grow min-h-0">
                    <AfterTaxIncomeChart labels={labels} chartData={chartData} />
                  </div>
                </div>
              ) : (
                <div className="h-full flex items-center justify-center min-h-[400px]">
                  <div className="flex flex-col items-center gap-4">
                    {isLoading && (
                      <div className="relative">
                        <div className="animate-spin rounded-full h-12 w-12 border-4 border-primary/20 border-t-primary"></div>
                        <div className="absolute inset-0 rounded-full bg-gradient-to-r from-primary/10 to-accent/10"></div>
                      </div>
                    )}
                    <div className="text-center">
                      <div className="text-lg font-medium text-foreground mb-1">
                        {isLoading ? 'Optimizing your retirement strategy...' : 'Ready to optimize'}
                      </div>
                      <div className="text-sm text-muted-foreground">
                        {isLoading ? 'Processing complex optimization calculations' : 'Enter your retirement details to see optimization'}
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Enhanced Chart Summary Section */}
      {apiResponseData && chartData && !isLoading && (
        <div className="mt-6">
          <Card className="shadow-lg">
            <CardHeader>
              <div className="flex items-center gap-3">
                <div className="w-2 h-8 bg-gradient-to-b from-success to-primary rounded-full"></div>
                <div>
                  <h2 className="text-xl font-semibold text-card-foreground">Optimization Analysis</h2>
                  <p className="text-sm text-muted-foreground">
                    Key insights and recommendations from your retirement strategy optimization
                  </p>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <ChartSummary 
                summaryItems={generateAfterTaxRetirementSummary(apiResponseData)}
                title=""
              />
            </CardContent>
          </Card>
        </div>
      )}

      {/* Professional Terms Section */}
      <div className="mt-6">
        <Card className="shadow-lg">
          <CardHeader>
            <div className="flex items-center gap-3">
              <div className="w-2 h-8 bg-gradient-to-b from-muted-foreground to-secondary rounded-full"></div>
              <div>
                <h2 className="text-xl font-semibold text-card-foreground">Understanding Retirement Optimization</h2>
                <p className="text-sm text-muted-foreground">
                  Learn about the advanced concepts in retirement income optimization
                </p>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <TermsDescriptions 
              terms={afterTaxRetirementTerms} 
              title=""
            />
          </CardContent>
        </Card>
      </div>
      
      {/* Enhanced Error Display */}
      {showSnackbar && (
        <div className="fixed bottom-4 left-4 right-4 z-50 max-w-md mx-auto">
          <div className="bg-destructive text-destructive-foreground p-4 rounded-lg shadow-lg border border-destructive/20 backdrop-blur-sm">
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 bg-destructive-foreground rounded-full"></div>
              <div className="font-medium">Optimization Error</div>
            </div>
            <div className="mt-1 text-sm opacity-90">
              {snackbarMessage}
            </div>
          </div>
        </div>
      )}
      
      <FloatingChartButton
        chartRef={chartRef}
        chartData={chartData}
      />
    </div>
  );
};

export default AfterTaxRetirementIncomeOptimizerPage;
